import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleDot,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { formatDate } from "@/lib/utils";
import {
  usePhases,
  useCreatePhase,
  useUpdatePhase,
  useUpdatePhaseStatus,
  useDeletePhase,
  useReorderPhases,
  useCreateStage,
  useUpdateStage,
  useUpdateStageStatus,
  useDeleteStage,
  type PhaseDto,
  type StageDto,
} from "../hooks/usePhases";

const CARD_BORDER = "hsl(210,30%,85%)";

const STATUS_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Pending" },
  active: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Active" },
  completed: { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)", label: "Completed" },
  on_hold: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "On Hold" },
};

const STATUS_OPTIONS = ["pending", "active", "completed", "on_hold"] as const;

function StatusPill({
  status,
  onSelect,
}: {
  status: string;
  onSelect: (s: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.pending;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
        style={{ background: s.bg, color: s.fg }}
      >
        {s.label}
        <ChevronDown className="ml-1 h-3 w-3" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full z-50 mt-1 w-36 overflow-hidden rounded-md border bg-white shadow-md">
            {STATUS_OPTIONS.map((opt) => {
              const so = STATUS_STYLES[opt];
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onSelect(opt);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-muted"
                >
                  <span>{so.label}</span>
                  {opt === status && <Check className="h-3 w-3" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  count,
  label,
  tint,
  fg,
}: {
  count: number;
  label: string;
  tint: string;
  fg: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card p-4">
      <div
        className="flex h-9 w-9 items-center justify-center rounded-md text-xs font-semibold"
        style={{ background: tint, color: fg }}
      >
        {count}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

type StageDraft = {
  stageValue: string;
  startDate: string;
  endDate: string;
};

function PhaseModal({
  projectId,
  mode,
  initialPhase,
  onClose,
}: {
  projectId: string;
  mode: "create" | "edit";
  initialPhase?: PhaseDto;
  onClose: () => void;
}) {
  const phaseOptions = useCategoryItems("project_phase");
  // All stages are loaded once; per-phase filtering happens client-side
  // so the dropdown reacts instantly when the user picks a phase.
  const stageOptions = useCategoryItems("project_stage");
  const createPhase = useCreatePhase(projectId);
  const updatePhase = useUpdatePhase(projectId);

  const [phaseValue, setPhaseValue] = useState(
    initialPhase?.phaseValue ?? "",
  );
  const [description, setDescription] = useState(
    initialPhase?.description ?? "",
  );
  const [startDate, setStartDate] = useState(
    initialPhase?.startDate?.slice(0, 10) ?? "",
  );
  const [endDate, setEndDate] = useState(
    initialPhase?.endDate?.slice(0, 10) ?? "",
  );
  const [stages, setStages] = useState<StageDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submitting = createPhase.isPending || updatePhase.isPending;

  const stagesForCurrentPhase = useMemo(
    () =>
      (stageOptions.data ?? []).filter((s) => s.parentValue === phaseValue),
    [stageOptions.data, phaseValue],
  );

  // If the user changes phase after picking inline stages, drop any drafts
  // that no longer belong to the new phase — otherwise we'd submit values
  // the backend would happily accept but the filtered picker can't show.
  useEffect(() => {
    if (!phaseValue) {
      if (stages.length > 0) setStages([]);
      return;
    }
    setStages((current) => {
      const allowed = new Set(stagesForCurrentPhase.map((s) => s.value));
      const next = current.map((s) =>
        s.stageValue && !allowed.has(s.stageValue)
          ? { ...s, stageValue: "" }
          : s,
      );
      const changed = next.some((s, i) => s.stageValue !== current[i].stageValue);
      return changed ? next : current;
    });
  }, [phaseValue, stagesForCurrentPhase, stages.length]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!phaseValue) {
      setError("Phase is required");
      return;
    }
    const phaseLabel =
      phaseOptions.data?.find((i) => i.value === phaseValue)?.label ??
      phaseValue;
    try {
      if (mode === "create") {
        await createPhase.mutateAsync({
          name: phaseLabel,
          phaseValue,
          description: description || null,
          startDate: startDate || null,
          endDate: endDate || null,
          stages: stages
            .filter((s) => s.stageValue)
            .map((s) => {
              const label =
                stageOptions.data?.find((i) => i.value === s.stageValue)
                  ?.label ?? s.stageValue;
              return {
                name: label,
                stageValue: s.stageValue,
                startDate: s.startDate || null,
                endDate: s.endDate || null,
              };
            }),
        });
      } else if (initialPhase) {
        await updatePhase.mutateAsync({
          phaseId: initialPhase.id,
          payload: {
            name: phaseLabel,
            phaseValue,
            description: description || null,
            startDate: startDate || null,
            endDate: endDate || null,
          },
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save phase");
    }
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold">
            {mode === "create" ? "Add Phase" : "Edit Phase"}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label>Phase <span className="text-destructive">*</span></Label>
              <Select
                value={phaseValue}
                onChange={(e) => setPhaseValue(e.target.value)}
              >
                <option value="">Select a phase...</option>
                {phaseOptions.data?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {mode === "create" && (
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label>Stages (optional)</Label>
                  <button
                    type="button"
                    disabled={!phaseValue}
                    onClick={() =>
                      setStages((s) => [
                        ...s,
                        { stageValue: "", startDate: "", endDate: "" },
                      ])
                    }
                    className="text-xs text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                  >
                    + Add Another Stage
                  </button>
                </div>
                {stages.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    {phaseValue
                      ? "Add stages to this phase"
                      : "Select a phase first"}
                  </p>
                )}
                {stages.map((s, idx) => (
                  <div
                    key={idx}
                    className="flex items-end gap-2 rounded-md border bg-muted/30 p-2"
                  >
                    <div className="flex-1 space-y-1">
                      <Select
                        value={s.stageValue}
                        disabled={!phaseValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          setStages((list) =>
                            list.map((it, i) =>
                              i === idx ? { ...it, stageValue: v } : it,
                            ),
                          );
                        }}
                      >
                        <option value="">
                          {phaseValue ? "Select stage..." : "Select a phase first"}
                        </option>
                        {stagesForCurrentPhase.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <Input
                      type="date"
                      value={s.startDate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setStages((list) =>
                          list.map((it, i) =>
                            i === idx ? { ...it, startDate: v } : it,
                          ),
                        );
                      }}
                      className="w-36"
                    />
                    <Input
                      type="date"
                      value={s.endDate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setStages((list) =>
                          list.map((it, i) =>
                            i === idx ? { ...it, endDate: v } : it,
                          ),
                        );
                      }}
                      className="w-36"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setStages((list) => list.filter((_, i) => i !== idx))
                      }
                      className="rounded p-1 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              {mode === "create" ? "Create Phase" : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}

function StageModal({
  projectId,
  phase,
  stage,
  onClose,
}: {
  projectId: string;
  phase: PhaseDto;
  stage?: StageDto;
  onClose: () => void;
}) {
  // Fetch only the stages whose parentValue matches this phase. The Stage
  // picker is locked to a known phase here, so server-side filtering is
  // both safe and slightly cheaper.
  const stageOptions = useCategoryItems("project_stage", phase.phaseValue);
  const createStage = useCreateStage(projectId);
  const updateStage = useUpdateStage(projectId);
  const [stageValue, setStageValue] = useState(stage?.stageValue ?? "");
  const [description, setDescription] = useState(stage?.description ?? "");
  const [startDate, setStartDate] = useState(
    stage?.startDate?.slice(0, 10) ?? "",
  );
  const [endDate, setEndDate] = useState(stage?.endDate?.slice(0, 10) ?? "");
  const [error, setError] = useState<string | null>(null);
  const submitting = createStage.isPending || updateStage.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stageValue) {
      setError("Stage is required");
      return;
    }
    const label =
      stageOptions.data?.find((i) => i.value === stageValue)?.label ??
      stageValue;
    try {
      if (stage) {
        await updateStage.mutateAsync({
          phaseId: phase.id,
          stageId: stage.id,
          payload: {
            name: label,
            stageValue,
            description: description || null,
            startDate: startDate || null,
            endDate: endDate || null,
          },
        });
      } else {
        await createStage.mutateAsync({
          phaseId: phase.id,
          payload: {
            name: label,
            stageValue,
            description: description || null,
            startDate: startDate || null,
            endDate: endDate || null,
          },
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save stage");
    }
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 z-[9999] flex h-full w-[480px] flex-col border-l bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold">
            {stage ? "Edit Stage" : `Add Stage to ${phase.name}`}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label>Stage <span className="text-destructive">*</span></Label>
              <Select
                value={stageValue}
                onChange={(e) => setStageValue(e.target.value)}
              >
                <option value="">Select a stage...</option>
                {stageOptions.data?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              {stage ? "Save Changes" : "Add Stage"}
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}

function StageRow({
  projectId,
  phase,
  stage,
}: {
  projectId: string;
  phase: PhaseDto;
  stage: StageDto;
}) {
  const updateStatus = useUpdateStageStatus(projectId);
  const deleteStage = useDeleteStage(projectId);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  function nextStatus(current: string): string {
    if (current === "pending") return "active";
    if (current === "active") return "completed";
    return "pending";
  }

  const Icon =
    stage.status === "completed"
      ? Check
      : stage.status === "active"
        ? CircleDot
        : Circle;
  const iconColor =
    stage.status === "completed"
      ? "text-emerald-600"
      : stage.status === "active"
        ? "text-blue-600"
        : "text-muted-foreground";

  const date =
    stage.startDate || stage.endDate
      ? `${stage.startDate ? formatDate(stage.startDate) : ""}${
          stage.endDate ? ` → ${formatDate(stage.endDate)}` : ""
        }`
      : "";

  const s = STATUS_STYLES[stage.status] ?? STATUS_STYLES.pending;

  return (
    <>
      <div className="flex items-center gap-3 rounded-md border border-transparent px-2 py-1.5 hover:bg-muted/30">
        <button
          type="button"
          title="Toggle status"
          onClick={() =>
            updateStatus.mutate({
              phaseId: phase.id,
              stageId: stage.id,
              status: nextStatus(stage.status),
            })
          }
          className={iconColor}
        >
          <Icon className="h-4 w-4" />
        </button>
        <div className="flex-1 text-[13px]">{stage.name}</div>
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
          style={{ background: s.bg, color: s.fg }}
        >
          {s.label}
        </span>
        {date && (
          <span className="text-[11px] text-muted-foreground">{date}</span>
        )}
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className="rounded p-1 text-muted-foreground hover:bg-muted"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {editOpen && (
        <StageModal
          projectId={projectId}
          phase={phase}
          stage={stage}
          onClose={() => setEditOpen(false)}
        />
      )}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete stage?"
        description={`Are you sure you want to delete the stage '${stage.name}'? It will be removed from this phase.`}
        onConfirm={async () => {
          await deleteStage.mutateAsync({ phaseId: phase.id, stageId: stage.id });
        }}
      />
    </>
  );
}

function PhaseCard({
  projectId,
  phase,
  index,
  total,
  onReorder,
  onTabChange,
}: {
  projectId: string;
  phase: PhaseDto;
  index: number;
  total: number;
  onReorder: (id: string, dir: -1 | 1) => void;
  onTabChange?: (tab: string, phaseId: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [addStageOpen, setAddStageOpen] = useState(false);
  const [editPhaseOpen, setEditPhaseOpen] = useState(false);
  const [deletePhaseOpen, setDeletePhaseOpen] = useState(false);
  const updateStatus = useUpdatePhaseStatus(projectId);
  const deletePhase = useDeletePhase(projectId);

  const dateRange =
    phase.startDate || phase.endDate
      ? `${phase.startDate ? formatDate(phase.startDate) : ""}${
          phase.endDate ? ` → ${formatDate(phase.endDate)}` : ""
        }`
      : null;

  return (
    <>
      <div
        className="rounded-xl border bg-card"
        style={{ borderColor: CARD_BORDER }}
      >
        <div className="flex items-center gap-3 px-5 py-3">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="text-muted-foreground"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <div className="flex-1">
            <div className="text-[15px] font-semibold">{phase.name}</div>
            {dateRange && (
              <div className="text-[12px] text-muted-foreground">
                {dateRange}
              </div>
            )}
          </div>
          <StatusPill
            status={phase.status}
            onSelect={(status) =>
              updateStatus.mutate({ phaseId: phase.id, status })
            }
          />
          <div className="flex items-center gap-2">
            <div
              className="h-1.5 w-[100px] overflow-hidden rounded-full"
              style={{ background: "hsl(210,30%,93%)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${phase.progress}%`,
                  background: "hsl(210,100%,45%)",
                }}
              />
            </div>
            <span className="w-8 text-right text-[11px] text-muted-foreground">
              {phase.progress}%
            </span>
          </div>
        </div>

        {expanded && (
          <div className="border-t px-5 py-3">
            <div className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              Stages
            </div>
            <div className="space-y-1">
              {phase.stages.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No stages yet
                </p>
              )}
              {phase.stages.map((stage) => (
                <StageRow
                  key={stage.id}
                  projectId={projectId}
                  phase={phase}
                  stage={stage}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setAddStageOpen(true)}
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-dashed px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted"
            >
              <Plus className="h-3 w-3" />
              Add Stage
            </button>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3">
              {(
                [
                  ["tasks", "Tasks"],
                  ["meetings", "Meetings"],
                  ["actions", "Actions"],
                  ["notes", "Notes"],
                  ["ideas", "Ideas"],
                  ["decisions", "Decisions"],
                  ["milestones", "Milestones"],
                  ["budgets", "Budgets"],
                  ["expenses", "Expenses"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => onTabChange?.(key, phase.id)}
                  className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] hover:bg-muted"
                >
                  {label}:{" "}
                  <span className="font-semibold">
                    {(phase.counts as Record<string, number>)[key] ?? 0}
                  </span>
                </button>
              ))}

              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onReorder(phase.id, -1)}
                  disabled={index === 0}
                  className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-40"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onReorder(phase.id, 1)}
                  disabled={index === total - 1}
                  className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-40"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditPhaseOpen(true)}
                >
                  <Pencil className="mr-1 h-3 w-3" /> Edit Phase
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeletePhaseOpen(true)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="mr-1 h-3 w-3" /> Delete Phase
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {addStageOpen && (
        <StageModal
          projectId={projectId}
          phase={phase}
          onClose={() => setAddStageOpen(false)}
        />
      )}
      {editPhaseOpen && (
        <PhaseModal
          projectId={projectId}
          mode="edit"
          initialPhase={phase}
          onClose={() => setEditPhaseOpen(false)}
        />
      )}
      <ConfirmDialog
        open={deletePhaseOpen}
        onOpenChange={setDeletePhaseOpen}
        title="Delete phase?"
        description={`Are you sure you want to delete the phase '${phase.name}'? Its stages will also be removed.`}
        onConfirm={async () => {
          await deletePhase.mutateAsync(phase.id);
        }}
      />
    </>
  );
}

export function PhasesTab({
  projectId,
  onTabChange,
}: {
  projectId: string;
  onTabChange?: (tab: string, phaseId: string) => void;
}) {
  const phases = usePhases(projectId);
  const reorder = useReorderPhases(projectId);
  const [addOpen, setAddOpen] = useState(false);

  const stats = useMemo(() => {
    const list = phases.data ?? [];
    return {
      total: list.length,
      active: list.filter((p) => p.status === "active").length,
      completed: list.filter((p) => p.status === "completed").length,
      pending: list.filter((p) => p.status === "pending").length,
    };
  }, [phases.data]);

  function handleReorder(phaseId: string, dir: -1 | 1) {
    const list = phases.data ?? [];
    const idx = list.findIndex((p) => p.id === phaseId);
    if (idx === -1) return;
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(target, 0, moved);
    reorder.mutate(
      reordered.map((p, i) => ({ id: p.id, sortOrder: i })),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard
            count={stats.total}
            label="Total Phases"
            tint="hsl(210,55%,92%)"
            fg="hsl(210,100%,28%)"
          />
          <SummaryCard
            count={stats.active}
            label="Active"
            tint="hsl(142,55%,92%)"
            fg="hsl(142,72%,28%)"
          />
          <SummaryCard
            count={stats.completed}
            label="Completed"
            tint="hsl(175,55%,92%)"
            fg="hsl(175,60%,30%)"
          />
          <SummaryCard
            count={stats.pending}
            label="Pending"
            tint="hsl(210,16%,93%)"
            fg="hsl(215,16%,40%)"
          />
        </div>
        <Button className="ml-4" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Add Phase
        </Button>
      </div>

      {phases.isLoading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!phases.isLoading && (phases.data ?? []).length === 0 && (
        <div className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">
          No phases yet. Click “Add Phase” to create the first one.
        </div>
      )}

      <div className="space-y-3">
        {(phases.data ?? []).map((phase, i, arr) => (
          <PhaseCard
            key={phase.id}
            projectId={projectId}
            phase={phase}
            index={i}
            total={arr.length}
            onReorder={handleReorder}
            onTabChange={onTabChange}
          />
        ))}
      </div>

      {addOpen && (
        <PhaseModal
          projectId={projectId}
          mode="create"
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}
